"use client"

import { useTr } from "../../lib/use-tr"
import {
  Check,
  ChevronDown,
  Globe,
  Inbox,
  Layers,
  MapPin,
  Search,
  Send,
  Sparkles,
  Star,
} from "lucide-react"
import { ComponentType, ReactNode } from "react"

import { cn } from "@/lib/utils"

import { PROCESS_T } from "./process-translations"

// Re-exported so the Process block keeps a single import site.
export { PROCESS_T }

type IconCmp = ComponentType<{ className?: string; "aria-hidden"?: boolean }>

export type Step = {
  num: number
  icon: IconCmp
  titleKey: keyof typeof PROCESS_T
  bodyKey: keyof typeof PROCESS_T
  visual: () => ReactNode
}

export const STEPS: ReadonlyArray<Step> = [
  {
    num: 1,
    icon: Search,
    titleKey: "step_1_title",
    bodyKey: "step_1_body",
    visual: () => <Step1Visual />,
  },
  {
    num: 2,
    icon: Globe,
    titleKey: "step_2_title",
    bodyKey: "step_2_body",
    visual: () => <Step2Visual />,
  },
  {
    num: 3,
    icon: Layers,
    titleKey: "step_3_title",
    bodyKey: "step_3_body",
    visual: () => <Step3Visual />,
  },
  {
    num: 4,
    icon: Send,
    titleKey: "step_4_title",
    bodyKey: "step_4_body",
    visual: () => <Step4Visual />,
  },
  {
    num: 5,
    icon: Inbox,
    titleKey: "step_5_title",
    bodyKey: "step_5_body",
    visual: () => <Step5Visual />,
  },
] as const

function Step1Visual() {
  const tr = useTr(PROCESS_T)
  const chips = [
    { key: "v_step1_chip_dentist", active: true },
    { key: "v_step1_chip_agency", active: false },
    { key: "v_step1_chip_hotel", active: false },
    { key: "v_step1_chip_gym", active: false },
    { key: "v_step1_chip_restaurant", active: false },
    { key: "v_step1_chip_law", active: false },
  ] as const
  return (
    <div className="bg-background ring-border/60 w-full max-w-sm rounded-xl p-4 shadow-sm ring-1">
      <div className="text-muted-foreground/80 mb-3 flex items-center gap-2 text-xs">
        <Search className="size-3.5" aria-hidden />
        <span className="font-mono tracking-wide uppercase">
          {tr("step_1_title")}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {chips.map(({ key, active }) => (
          <span
            key={key}
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1",
              active
                ? "bg-primary-cta/10 text-primary-cta-text ring-primary-cta/30"
                : "text-muted-foreground ring-border/60 bg-card",
            )}
          >
            {active ? <Check className="size-3" aria-hidden /> : null}
            {tr(key)}
          </span>
        ))}
      </div>
    </div>
  )
}

function Step2Visual() {
  const tr = useTr(PROCESS_T)
  const cities = [
    { key: "v_step2_city_lyon", active: true },
    { key: "v_step2_city_berlin", active: false },
    { key: "v_step2_city_lisbon", active: false },
    { key: "v_step2_city_tokyo", active: false },
  ] as const
  return (
    <div className="bg-background ring-border/60 w-full max-w-sm rounded-xl p-4 shadow-sm ring-1">
      <div className="border-border/60 mb-2 flex items-center gap-2 rounded-lg border px-3 py-2">
        <Globe className="text-muted-foreground/80 size-4" aria-hidden />
        <span className="text-foreground text-2sm flex-1">
          {tr("v_step2_city_lyon")}
        </span>
        <ChevronDown
          className="text-muted-foreground/60 size-3.5"
          aria-hidden
        />
      </div>
      <ul className="flex flex-col gap-0.5">
        {cities.map(({ key, active }) => (
          <li
            key={key}
            className={cn(
              "text-2sm flex items-center gap-2 rounded-md px-2.5 py-1.5",
              active
                ? "bg-primary-cta/8 text-foreground"
                : "text-muted-foreground",
            )}
          >
            <MapPin className="size-3.5" aria-hidden />
            <span>{tr(key)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Step3Visual() {
  const tr = useTr(PROCESS_T)
  const items = [
    { key: "v_step3_email", on: true, meta: "v_step3_included" },
    { key: "v_step3_phone", on: true, meta: "+0.2x" },
    { key: "v_step3_seo", on: true, meta: "+0.5x" },
    { key: "v_step3_ads", on: true, meta: "+0.3x" },
    { key: "v_step3_ratings", on: false, meta: "+0.2x" },
  ] as const
  return (
    <div className="bg-background ring-border/60 w-full max-w-sm rounded-xl p-4 shadow-sm ring-1">
      <ul className="flex flex-col gap-2">
        {items.map(({ key, on, meta }) => {
          const isIncludedKey = meta === "v_step3_included"
          return (
            <li
              key={key}
              className="text-2sm flex items-center justify-between gap-3"
            >
              <span
                className={cn(
                  "flex items-center gap-2",
                  on ? "text-foreground" : "text-muted-foreground/70",
                )}
              >
                <span
                  className={cn(
                    "inline-flex size-4 items-center justify-center rounded-full",
                    on
                      ? "bg-primary-cta text-primary-cta-foreground"
                      : "ring-border/60 bg-card ring-1",
                  )}
                >
                  {on ? <Check className="size-2.5" aria-hidden /> : null}
                </span>
                {tr(key)}
              </span>
              <span className="text-muted-foreground text-2xs font-mono">
                {isIncludedKey ? tr(meta) : meta}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function Step4Visual() {
  const tr = useTr(PROCESS_T)
  const messages = [
    { key: "v_step4_d0", emphasis: true },
    { key: "v_step4_d3", emphasis: false },
    { key: "v_step4_d7", emphasis: false },
  ] as const
  return (
    <div className="bg-background ring-border/60 w-full max-w-sm rounded-xl p-4 shadow-sm ring-1">
      <div className="text-muted-foreground/80 mb-3 flex items-center justify-between text-xs">
        <span className="font-mono tracking-wide uppercase">
          {tr("v_step4_seq")}
        </span>
        <span className="text-primary-cta-text text-2xs inline-flex items-center gap-1">
          <Send className="size-3" aria-hidden />
          {tr("v_step4_pushed")}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {messages.map(({ key, emphasis }, idx) => (
          <div
            key={key}
            className={cn(
              "border-border/60 flex items-center gap-3 rounded-lg border px-3 py-2.5",
              emphasis && "border-primary-cta/30 bg-primary-cta/5",
            )}
          >
            <span
              className={cn(
                "text-2xs inline-flex size-6 items-center justify-center rounded-md font-mono font-medium",
                emphasis
                  ? "bg-primary-cta text-primary-cta-foreground"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {idx + 1}
            </span>
            <span className="text-foreground text-2sm">{tr(key)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Step5Visual() {
  const tr = useTr(PROCESS_T)
  const leads = [
    {
      nameKey: "v_step5_lead1_name",
      score: 8.2,
      statusKey: "v_step5_status_open",
      statusTone: "good" as const,
    },
    {
      nameKey: "v_step5_lead2_name",
      score: 7.1,
      statusKey: "v_step5_status_sent",
      statusTone: "neutral" as const,
    },
    {
      nameKey: "v_step5_lead3_name",
      score: 6.4,
      statusKey: "v_step5_status_queued",
      statusTone: "muted" as const,
    },
  ] as const
  return (
    <div className="bg-background ring-border/60 w-full max-w-sm rounded-xl p-4 shadow-sm ring-1">
      <div className="text-muted-foreground/80 text-2xs mb-2 grid grid-cols-[1fr_auto_auto] items-center gap-3 px-1 font-mono tracking-wide uppercase">
        <span>{tr("v_step5_col_lead")}</span>
        <span>{tr("v_step5_col_score")}</span>
        <span>{tr("v_step5_col_status")}</span>
      </div>
      <ul className="flex flex-col gap-1">
        {leads.map(({ nameKey, score, statusKey, statusTone }) => (
          <li
            key={nameKey}
            className="border-border/40 grid grid-cols-[1fr_auto_auto] items-center gap-3 rounded-md border px-2.5 py-2 text-xs"
          >
            <span className="text-foreground truncate font-medium">
              {tr(nameKey)}
            </span>
            <span className="text-muted-foreground inline-flex items-center gap-1 font-mono">
              <Star className="text-primary-cta size-3" aria-hidden />
              {score.toFixed(1)}
            </span>
            <span
              className={cn(
                "text-3xs rounded-full px-2 py-0.5 font-medium ring-1",
                statusTone === "good" &&
                  "bg-primary-cta/10 text-primary-cta-text ring-primary-cta/30",
                statusTone === "neutral" &&
                  "bg-card text-foreground ring-border/60",
                statusTone === "muted" &&
                  "bg-muted text-muted-foreground ring-border/40",
              )}
            >
              {tr(statusKey)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function ProcessHeading() {
  const tr = useTr(PROCESS_T)
  return (
    <div className="mb-12 max-w-2xl text-left">
      <div className="bg-card/60 text-muted-foreground mb-5 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-sm">
        <Sparkles className="text-primary-cta size-3.5" aria-hidden />
        <span>{tr("eyebrow")}</span>
      </div>
      <h2 className="text-foreground font-display leading-display tracking-dense text-3xl font-semibold sm:text-4xl md:text-5xl">
        {tr("headline")}
      </h2>
      <p className="text-muted-foreground text-md mt-4 max-w-md leading-relaxed sm:text-base">
        {tr("subhead")}
      </p>
    </div>
  )
}
